/**
 * O hook de gravação de áudio da tela de WhatsApp (8.4, FR-41).
 *
 * Ele existe porque o critério de aceite é literal: "áudio gravado dentro do CRM chega **como
 * áudio** no WhatsApp". Gravar é a metade que o navegador faz, e ela é feita com o que o navegador
 * já tem — `getUserMedia` e `MediaRecorder`, sem biblioteca nenhuma. Nenhuma entra: uma dependência
 * de gravação traria codec, worker e polyfill para resolver um problema que duas APIs padrão
 * resolvem.
 *
 * **Três coisas dão errado de verdade aqui, e as três têm resposta escrita:**
 *
 * 1. **o navegador não suporta.** `MediaRecorder` não existe em todo lugar (e não existe no jsdom),
 *    e um `undefined is not a constructor` no clique seria a tela quebrando sem mensagem. O hook
 *    responde com uma recusa legível e o botão nem aparece;
 * 2. **a pessoa nega o microfone.** É o caso mais comum, e ele **não** é erro: ela decidiu. A
 *    recusa diz o que fazer (liberar o microfone), e nada fica gravando em segundo plano;
 * 3. **a gravação passa do teto.** Sem ele, um microfone esquecido aberto viraria um arquivo de
 *    dezenas de MB que o serviço recusa no fim — depois de a pessoa esperar o upload inteiro. O
 *    hook para sozinho no teto e entrega o que gravou.
 *
 * **A trilha é liberada ao parar, sempre.** Um `MediaStreamTrack` que fica vivo mantém a luz do
 * microfone acesa e o navegador anunciando que a aba está gravando — e a pessoa não tem como
 * desligar isso a não ser fechando a aba. Isso vale no fim normal, no cancelamento e na
 * desmontagem do componente.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

/** O que o hook entrega quando a gravação termina: os bytes e quanto tempo eles duram. */
export interface AudioGravado {
  /** O áudio, no formato que o navegador escolheu — o `type` dele é o mime a enviar. */
  arquivo: Blob
  /** Quantos segundos, arredondados — é o que vira `duracao_em_segundos` na mensagem. */
  duracaoEmSegundos: number
}

/**
 * O teto de uma gravação, em segundos.
 *
 * Cinco minutos é muito acima de qualquer recado de vendedor e muito abaixo do teto de tamanho do
 * serviço: em `audio/ogg` isso dá algumas centenas de KB, e o serviço aceita 32 MB. O que o teto
 * evita não é o arquivo grande — é o **microfone esquecido aberto**, que grava até a aba fechar.
 */
export const DURACAO_MAXIMA_EM_SEGUNDOS = 300

/** De quanto em quanto o relógio da gravação avança na tela, em milissegundos. */
const PASSO_DO_RELOGIO = 1_000

export const SEM_SUPORTE_A_GRAVACAO =
  'Este navegador não grava áudio. Use o anexo para enviar um arquivo de som.'

export const MICROFONE_NEGADO =
  'O microfone não foi liberado. Autorize o acesso nas permissões do navegador e tente de novo.'

export const FALHA_NA_GRAVACAO = 'Não foi possível gravar o áudio. Tente de novo.'

/**
 * Se este navegador consegue gravar — a pergunta que decide se o botão do microfone existe.
 *
 * Ela é uma função, e não uma constante de módulo, porque o jsdom instala os polyfills **por
 * teste** (`preparacao-de-testes.ts`): avaliada no import, ela congelaria o valor do primeiro
 * arquivo carregado.
 */
export function navegadorGravaAudio(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices !== undefined &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  )
}

/**
 * Grava um áudio pelo microfone, e entrega o arquivo quando ela termina.
 *
 * `aoTerminar` recebe o áudio **só no fim normal**: cancelar não chama nada, e é isso que separa
 * "desisti" de "gravei". O componente é quem decide o que fazer com o arquivo (aqui, enviá-lo), e
 * este hook não conhece rede nenhuma.
 */
export function useGravacaoDeAudio(aoTerminar: (audio: AudioGravado) => void) {
  const [gravando, definirGravando] = useState(false)
  const [segundos, definirSegundos] = useState(0)
  const [recusa, definirRecusa] = useState<string | null>(null)

  const gravador = useRef<MediaRecorder | null>(null)
  const trilha = useRef<MediaStream | null>(null)
  const pedacos = useRef<Blob[]>([])
  const relogio = useRef<ReturnType<typeof setInterval> | null>(null)
  const cancelada = useRef(false)
  /*
   * O "cancelar" que chega **antes** de o gravador existir — enquanto o navegador ainda pergunta pelo
   * microfone (a conversa virou de outro número, 13.11c). Sem esta marca, a permissão concedida
   * depois começaria uma gravação sem controle nenhum na tela.
   */
  const desistiuAntes = useRef(false)
  const decorridos = useRef(0)
  /*
   * O callback num `ref` porque ele entra num `onstop` do `MediaRecorder`, que é registrado uma vez
   * e vive até a gravação acabar: capturado por closure, ele congelaria a versão do render em que a
   * gravação começou — e o envio dispararia com o `conversaId` de outra conversa se a pessoa
   * trocasse de chat no meio.
   */
  const aoTerminarRef = useRef(aoTerminar)
  //: A escrita acontece num efeito, e nao no corpo do render: escrever num `ref` durante o render
  //: e o que o `react-hooks/refs` recusa — e com razao, porque num render descartado (o modo
  //: concorrente do React) a escrita ficaria de pe sobre um estado que nunca existiu.
  useEffect(() => {
    aoTerminarRef.current = aoTerminar
  }, [aoTerminar])

  /** Desliga o relógio e **libera a trilha** — o passo que não pode faltar em caminho nenhum. */
  const soltar = useCallback(() => {
    if (relogio.current !== null) {
      clearInterval(relogio.current)
      relogio.current = null
    }
    trilha.current?.getTracks().forEach((faixa) => {
      faixa.stop()
    })
    trilha.current = null
    gravador.current = null
  }, [])

  /*
   * A desmontagem solta tudo. Sem isto, sair da tela no meio de uma gravação deixaria o microfone
   * aberto — com a luz acesa e o aviso do navegador — até a aba fechar.
   */
  useEffect(() => soltar, [soltar])

  const comecar = useCallback(async () => {
    if (gravando) {
      return
    }
    definirRecusa(null)
    desistiuAntes.current = false
    if (!navegadorGravaAudio()) {
      definirRecusa(SEM_SUPORTE_A_GRAVACAO)
      return
    }
    let fluxo: MediaStream
    try {
      fluxo = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      // Negar o microfone **não** é erro: é uma decisão de quem está usando, e a recusa diz o que
      // fazer para mudá-la.
      definirRecusa(MICROFONE_NEGADO)
      return
    }
    if (desistiuAntes.current) {
      fluxo.getTracks().forEach((faixa) => {
        faixa.stop()
      })
      return
    }

    /*
     * **A trilha é guardada antes de o gravador ser construído**, e a ordem é o que fecha um
     * vazamento real: `new MediaRecorder(fluxo)` pode levantar (um `NotSupportedError` quando
     * nenhum codec serve, por exemplo), e com a atribuição depois dele o `soltar()` do `catch`
     * encontraria `trilha.current` ainda nula — o microfone ficaria **ligado**, com a luz acesa e o
     * aviso do navegador, até a aba fechar. E a pessoa não teria como desligá-lo.
     */
    trilha.current = fluxo
    try {
      const instancia = new MediaRecorder(fluxo)
      gravador.current = instancia
      pedacos.current = []
      cancelada.current = false
      decorridos.current = 0
      definirSegundos(0)

      instancia.ondataavailable = (evento: BlobEvent) => {
        if (evento.data.size > 0) {
          pedacos.current.push(evento.data)
        }
      }
      instancia.onstop = () => {
        const duracao = decorridos.current
        const partes = pedacos.current
        const foiCancelada = cancelada.current
        soltar()
        definirGravando(false)
        definirSegundos(0)
        if (foiCancelada || partes.length === 0) {
          return
        }
        aoTerminarRef.current({
          // O `type` sai do próprio gravador: é ele que sabe em que formato gravou, e adivinhá-lo
          // aqui produziria um mime que o serviço recusa por não bater com os bytes.
          arquivo: new Blob(partes, { type: instancia.mimeType || 'audio/webm' }),
          duracaoEmSegundos: Math.max(1, Math.round(duracao)),
        })
      }

      instancia.start()
      definirGravando(true)
      relogio.current = setInterval(() => {
        decorridos.current += 1
        definirSegundos(decorridos.current)
        if (decorridos.current >= DURACAO_MAXIMA_EM_SEGUNDOS) {
          // O teto **entrega** o que gravou, e não descarta: quem falou cinco minutos disse alguma
          // coisa, e jogar fora seria pior do que mandar o pedaço.
          gravador.current?.stop()
        }
      }, PASSO_DO_RELOGIO)
    } catch {
      soltar()
      definirGravando(false)
      definirRecusa(FALHA_NA_GRAVACAO)
    }
  }, [gravando, soltar])

  /** Termina a gravação e entrega o áudio. */
  const terminar = useCallback(() => {
    if (gravador.current === null) {
      return
    }
    cancelada.current = false
    gravador.current.stop()
  }, [])

  /** Desiste: a trilha é liberada e **nada** é entregue. */
  const cancelar = useCallback(() => {
    if (gravador.current === null) {
      desistiuAntes.current = true
      definirGravando(false)
      return
    }
    cancelada.current = true
    gravador.current.stop()
  }, [])

  /*
   * **`definirRecusa` não é devolvida**, e a ausência é deliberada: a recusa da gravação é do
   * navegador (sem suporte, microfone negado), e quem a produz é este hook. Um componente que
   * pudesse escrevê-la de fora teria duas fontes para o mesmo alerta — e a de fora seria a que
   * ninguém limparia. Ela é limpa aqui, no começo de cada tentativa.
   */
  return { gravando, segundos, recusa, comecar, terminar, cancelar }
}
